<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { planningApi } from '@/api'
import type { PlanningSchemeOverview, PlanningScheme } from '@/api'
import CreateSchemeModal from '@/components/modals/CreateSchemeModal.vue'
import { useAnalytics } from '@/composables/useAnalytics'
import { useGating } from '@/composables/useGating'
import {
  AppButton,
  AppCard,
  HomeScreenHeader,
  SchemeListRow,
  SectionEyebrow,
} from '@/design-system/components'
import { BookOpen, Check, Plus, Sparkles } from 'lucide-vue-next'

const { capture } = useAnalytics()
const { atSchemeCap, showPaywall, refreshUsage } = useGating()

const router = useRouter()

const schemes = ref<PlanningSchemeOverview[]>([])
const isLoading = ref(true)
const showCreateModal = ref(false)

function openCreateModal() {
  if (atSchemeCap.value) {
    showPaywall('scheme')
    return
  }
  showCreateModal.value = true
}

async function fetchSchemes() {
  isLoading.value = true
  try {
    schemes.value = await planningApi.getSchemes()
  } catch {
    schemes.value = []
  } finally {
    isLoading.value = false
  }
}

function onSchemeCreated(scheme: PlanningScheme) {
  capture('course_created', {
    has_description: !!scheme.description?.trim(),
    // Web planning schemes are not bound to an academic year, unlike Android
    // courses — kept for cross-platform schema parity.
    has_year: false,
    icon_id: scheme.iconId,
  })
  void refreshUsage()
  router.push(`/planning/${scheme.id}`)
}

onMounted(fetchSchemes)
</script>

<template>
  <div class="planning-page">
    <HomeScreenHeader
      title="Planning"
      subtitle="Organise your courses, units, and lesson plans"
    />

    <div class="planning-grid">
      <!-- ===== SCHEMES (2 cols) ===== -->
      <section class="planning-section">
        <SectionEyebrow label="Schemes of Work" :count="schemes.length">
          <template #actions>
            <AppButton
              v-if="schemes.length > 0"
              variant="primary"
              size="sm"
              @click="openCreateModal"
            >
              <Plus :size="14" />
              New
            </AppButton>
          </template>
        </SectionEyebrow>

        <!-- Loading -->
        <div v-if="isLoading" class="loading-rows">
          <div v-for="i in 4" :key="i" class="loading-row">
            <div class="loading-icon" />
            <div class="loading-body">
              <div class="loading-line w-40" />
              <div class="loading-line w-24 sub" />
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else-if="schemes.length === 0" class="empty-state">
          <div class="empty-icon">
            <BookOpen :size="28" :stroke-width="1.5" />
          </div>
          <p class="empty-title">No courses yet</p>
          <p class="empty-message">
            Create a scheme of work for each course you teach, like Year 7 Music or Year 8 Drama.
          </p>
          <AppButton variant="primary" size="md" @click="openCreateModal">
            <Plus :size="16" />
            Create your first course
          </AppButton>
        </div>

        <!-- Schemes list -->
        <div v-else class="list-rows">
          <SchemeListRow
            v-for="scheme in schemes"
            :key="scheme.id"
            :name="scheme.name ?? ''"
            :colour="scheme.colour"
            :icon-id="scheme.iconId"
            :unit-count="scheme.unitCount ?? 0"
            :lesson-plan-count="scheme.lessonPlanCount ?? 0"
            :to="`/planning/${scheme.id}`"
          />
          <hr class="list-close-hairline" aria-hidden="true" />
        </div>
      </section>

      <!-- ===== RIGHT SIDEBAR ===== -->
      <aside class="planning-sidebar">
        <AppCard variant="widget" padding="none" class="sidebar-widget">
          <div class="widget-header">
            <h2 class="widget-title">
              <Sparkles :size="16" />
              AI Planning
            </h2>
          </div>

          <div class="widget-body">
            <p class="widget-text">
              Let AI help you generate lesson plans, create activities, and build comprehensive
              schemes based on your curriculum.
            </p>
            <ul class="widget-bullets">
              <li><Check :size="14" /> Generate unit overviews</li>
              <li><Check :size="14" /> Create lesson plans with activities</li>
              <li><Check :size="14" /> Align to curriculum standards</li>
            </ul>
            <p class="widget-footnote">
              Available inside any scheme of work — look for the AI button.
            </p>
          </div>
        </AppCard>
      </aside>
    </div>

    <CreateSchemeModal v-model="showCreateModal" @created="onSchemeCreated" />
  </div>
</template>

<style scoped>
.planning-page {
  display: flex;
  flex-direction: column;
  gap: var(--space-xxl);
}

.planning-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-xl);
}

@media (min-width: 1024px) {
  .planning-grid {
    grid-template-columns: 2fr 1fr;
  }
}

.planning-section {
  background: var(--surface);
  border: var(--hairline-border);
  border-radius: var(--radius-xl);
  overflow: hidden;
}

.list-rows {
  display: block;
}

.list-close-hairline {
  border: 0;
  border-top: 1px solid var(--hairline);
  margin: 0;
}

/* Loading */
.loading-rows {
  padding: var(--space-md) var(--space-20);
}

.loading-row {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-md) 0;
}

.loading-icon {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-lg);
  background: var(--ink-100);
  animation: pulse 1.6s ease-in-out infinite;
  flex-shrink: 0;
}

.loading-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.loading-line {
  height: 12px;
  background: var(--ink-100);
  border-radius: var(--radius-sm);
  animation: pulse 1.6s ease-in-out infinite;
}

.loading-line.sub {
  background: var(--ink-50);
}

.w-24 { width: 96px; }
.w-40 { width: 160px; }

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}

/* Empty */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--space-xxxl) var(--space-xl);
  gap: var(--space-sm);
}

.empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: var(--radius-lg);
  background: var(--ink-50);
  color: var(--ink-500);
  margin-bottom: var(--space-sm);
}

.empty-title {
  font-size: var(--text-title-md);
  font-weight: var(--weight-semibold);
  color: var(--ink-900);
  margin: 0;
}

.empty-message {
  font-size: var(--text-body-sm);
  line-height: var(--text-body-sm-lh);
  color: var(--ink-500);
  max-width: 28rem;
  margin: 0 0 var(--space-md);
}

/* Sidebar */
.planning-sidebar {
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
}

.sidebar-widget {
  display: flex;
  flex-direction: column;
}

.widget-header {
  padding: var(--space-md) var(--space-lg);
  border-bottom: 1px solid var(--hairline);
}

.widget-title {
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: var(--text-title-sm);
  font-weight: var(--weight-semibold);
  color: var(--ink-900);
  margin: 0;
}

.widget-body {
  padding: var(--space-lg);
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.widget-text {
  font-size: var(--text-body-sm);
  line-height: var(--text-body-sm-lh);
  color: var(--ink-600);
  margin: 0;
}

.widget-bullets {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.widget-bullets li {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: var(--text-body-sm);
  color: var(--ink-700);
}

.widget-bullets svg {
  color: var(--ink-black);
  flex-shrink: 0;
}

.widget-footnote {
  font-size: var(--text-micro);
  line-height: var(--text-micro-lh);
  color: var(--ink-500);
  margin: var(--space-xs) 0 0;
}
</style>
